/*
  NBV2 Reviews (sections/nbv2-reviews.liquid)

  The track in assets/section-nbv2-reviews.css is a native scroll-snap
  container, so swipe, trackpad and keyboard already work without this file.
  All this script adds is the arrow pair: one card per click, and a disabled
  state at each end so the buttons never lie about what is left to scroll.

  At the mobile breakpoint the track is a centred one-card carousel that runs
  in a circle instead. A copy of the last card is prepended and a copy of the
  first is appended, so a neighbour is always visible on both sides -- including
  at the two ends, where there would otherwise be empty gutter. Scrolling onto
  one of those copies is silently swapped for the real card it stands in for
  once the scroll settles, which is invisible because the two are identical.

  The CSS owns that breakpoint and signals it through --nbv2-rev-loop; see
  assets/section-nbv2-reviews.css.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-nbv2-reviews]';
  var CARD_SELECTOR = '.nbv2-reviews__card';
  var CLONE_ATTR = 'data-nbv2-rev-clone';
  /* Long enough to outlast a smooth scroll, short enough to feel immediate. */
  var SETTLE_MS = 150;

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function init(root) {
    if (root.nbv2ReviewsInit === true) {
      return;
    }

    var track = root.querySelector('[data-nbv2-rev-track]');
    var nav = root.querySelector('[data-nbv2-rev-nav]');
    var prev = root.querySelector('[data-nbv2-rev-prev]');
    var next = root.querySelector('[data-nbv2-rev-next]');

    if (!track || !nav || !prev || !next) {
      return;
    }

    root.nbv2ReviewsInit = true;

    var ticking = false;
    var looped = false;
    var settleTimer = null;
    /* Set while this script moves the scroller, so its own scroll events are
       not mistaken for the reader reaching an end. */
    var teleporting = false;

    /*
      Loop mode is whatever the stylesheet currently says it is. Reading it per
      call rather than caching keeps a resize between breakpoints honest.
    */
    function looping() {
      return (
        window
          .getComputedStyle(root)
          .getPropertyValue('--nbv2-rev-loop')
          .trim() === '1'
      );
    }

    function cards() {
      return Array.prototype.slice.call(track.querySelectorAll(CARD_SELECTOR));
    }

    function realCards() {
      return cards().filter(function (card) {
        return !card.hasAttribute(CLONE_ATTR);
      });
    }

    /*
      One card plus one gap -- the distance between two neighbouring snap
      positions. Measuring the rendered card instead of recomputing the CSS
      formula keeps the two in step no matter which breakpoint is live.
    */
    function step() {
      var card = track.querySelector(CARD_SELECTOR);
      if (!card) {
        return track.clientWidth;
      }
      var gap = parseFloat(window.getComputedStyle(track).columnGap);
      if (isNaN(gap)) {
        gap = 0;
      }
      return card.offsetWidth + gap;
    }

    function scrollable() {
      /* 1px of slack: fractional layout widths make exact compares unreliable. */
      return track.scrollWidth - track.clientWidth > 1;
    }

    function jumpTo(left) {
      teleporting = true;
      if (track.scrollTo) {
        track.scrollTo({ left: left, behavior: 'auto' });
      } else {
        track.scrollLeft = left;
      }
      teleporting = false;
    }

    /*
      Build the circle. The leading and trailing copies take over as the
      track's :first-child and :last-child, so they inherit the centring
      margins the stylesheet puts there and centre exactly like a real card.
    */
    function buildLoop() {
      var list = realCards();
      if (looped || list.length < 2) {
        return;
      }

      var head = list[list.length - 1].cloneNode(true);
      var tail = list[0].cloneNode(true);

      [head, tail].forEach(function (clone) {
        clone.setAttribute(CLONE_ATTR, '');
        /* A duplicate must not be announced, or every review is read twice. */
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
      });

      track.insertBefore(head, track.firstChild);
      track.appendChild(tail);
      looped = true;

      /* Open on the real first card, one step past the leading copy. */
      jumpTo(step());
    }

    function teardownLoop() {
      if (!looped) {
        return;
      }

      cards().forEach(function (card) {
        if (card.hasAttribute(CLONE_ATTR)) {
          card.parentNode.removeChild(card);
        }
      });
      looped = false;
      jumpTo(0);
    }

    function syncLoop() {
      if (looping()) {
        buildLoop();
      } else {
        teardownLoop();
      }
    }

    /*
      Once a scroll settles on one of the copies, swap in the real card it
      stands for. Both are identical and both are centred, so the only thing
      that changes is how much runway is left on either side.
    */
    function recentre() {
      if (!looped) {
        return;
      }

      var unit = step();
      var max = track.scrollWidth - track.clientWidth;
      var count = realCards().length;

      if (track.scrollLeft <= 1) {
        jumpTo(count * unit);
      } else if (track.scrollLeft >= max - 1) {
        jumpTo(unit);
      }
    }

    function update() {
      ticking = false;

      if (looping()) {
        /* Wrapping arrows always have somewhere to go, so never disable them. */
        nav.hidden = realCards().length < 2;
        prev.disabled = false;
        next.disabled = false;
        return;
      }

      var overflows = scrollable();
      nav.hidden = !overflows;

      if (!overflows) {
        return;
      }

      var max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
    }

    function requestUpdate() {
      if (ticking) {
        return;
      }
      ticking = true;

      /* Frames only arrive while the tab is visible; fall back to a timer. */
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(update);
      } else {
        window.setTimeout(update, 16);
      }
    }

    function onScroll() {
      requestUpdate();

      if (!looped || teleporting) {
        return;
      }

      /*
        There is no portable "scroll finished" event across the browsers this
        theme supports, so settle on a quiet period instead. Recentring mid
        animation would fight the smooth scroll the arrows start.
      */
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(recentre, SETTLE_MS);
    }

    function onResize() {
      syncLoop();
      requestUpdate();
    }

    function scrollByCard(direction) {
      var distance = step() * direction;
      if (track.scrollBy) {
        track.scrollBy({
          left: distance,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      } else {
        track.scrollLeft += distance;
      }
    }

    function onPrev() {
      scrollByCard(-1);
    }

    function onNext() {
      scrollByCard(1);
    }

    prev.addEventListener('click', onPrev);
    next.addEventListener('click', onNext);
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    var observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(onResize);
      observer.observe(track);
    }

    syncLoop();
    update();

    root.nbv2ReviewsDestroy = function () {
      window.clearTimeout(settleTimer);
      prev.removeEventListener('click', onPrev);
      next.removeEventListener('click', onNext);
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (observer) {
        observer.disconnect();
      }
      teardownLoop();
      root.nbv2ReviewsInit = false;
      root.nbv2ReviewsDestroy = null;
    };
  }

  function initAll(scope) {
    var container = scope || document;
    var roots = Array.prototype.slice.call(container.querySelectorAll(ROOT_SELECTOR));

    if (container.nodeType === 1 && container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }

    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = Array.prototype.slice.call(event.target.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(function (root) {
      if (typeof root.nbv2ReviewsDestroy === 'function') {
        root.nbv2ReviewsDestroy();
      }
    });
  });
})();
